import type { ScheduleEntry, WorkPhase } from '@myapp/contract'
import { minutesBetween } from '../../../shared/ui/controls/TimeStepper/quarterStep'
import { formatDurationDe } from '../../../shared/lib/durationDe'
import { useElapsedSince } from '../../../shared/hooks/useElapsedSince'
import { formatTimeDe } from '../model/workSessionTime'

type Schedule = Pick<
  ScheduleEntry,
  | 'travelStartedAt'
  | 'actualWorkStartsAt'
  | 'actualEndsAt'
  | 'departureEndedAt'
  | 'plannedTravelMinutes'
  | 'plannedWorkMinutes'
  | 'plannedDepartureMinutes'
>

interface Props {
  schedule: Schedule
  phase: WorkPhase | null
  /** Offered only while the phase is ANFAHRT — a reset, not an edit. */
  onDiscardTravel?: () => void
}

/**
 * `Zeiten` — a mirror, not an editor. It shows what the system recorded and offers no way to
 * change it.
 *
 * That restraint is the point. Editing instants all day long would teach the technician that
 * tapping is optional, and the price of that is not minutes in a report — it is the live state
 * on `/api/schedule` that tells the dispatcher where he is right now, which no later correction
 * can reconstruct. Corrections therefore live only at a phase close.
 *
 * The one action here is `Anfahrt verwerfen`, and it is safe precisely because it is a reset:
 * it can only clear the stamp, never set a remembered time, so it cannot fabricate anything.
 *
 * Always visible — this used to hide behind a collapsed-by-default `Disclosure`, so a technician
 * mid-Anfahrt saw no elapsed context anywhere in the body. Status and time are the two facts a
 * worker standing at a door needs without tapping anything first.
 */
export function WorkSessionTimes({ schedule, phase, onDiscardTravel }: Props) {
  const rows = buildRows(schedule)
  const liveAnchor = phaseAnchor(schedule, phase)
  const ageMs = useElapsedSince(liveAnchor ? new Date(liveAnchor).getTime() : null)
  const liveMinutes = ageMs !== null ? Math.floor(ageMs / 60_000) : null
  const plannedForPhase = plannedMinutesForPhase(schedule, phase)
  const overrun = plannedForPhase > 0 && liveMinutes !== null && liveMinutes > plannedForPhase
  const liveLabel = phase === 'ANFAHRT' ? 'Anfahrt' : phase === 'ARBEIT' ? 'Arbeit' : phase === 'ABFAHRT' ? 'Abfahrt' : null

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Zeiten</p>

      <div className="mt-2 flex flex-col gap-2">
        {rows.length === 0 ? (
          <p className="text-sm text-gray-500">Noch nicht gestartet.</p>
        ) : (
          rows.map((row) => {
            const isLive = row.label === liveLabel && liveMinutes !== null
            return (
              <div key={row.label} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-gray-700">{row.label}</span>
                <span className="text-gray-900 tabular-nums">
                  {row.span}
                  {row.duration !== null && (
                    <span className="ml-2 font-medium">{formatDurationDe(row.duration)}</span>
                  )}
                  {isLive && (
                    <span
                      className={`ml-2 font-medium ${overrun ? 'text-amber-600' : ''}`}
                      aria-live="polite"
                    >
                      {formatDurationDe(liveMinutes as number)}
                    </span>
                  )}
                </span>
              </div>
            )
          })
        )}

        <p className="mt-1 text-xs text-gray-500">
          Geplant: Anfahrt {formatDurationDe(schedule.plannedTravelMinutes)} · Arbeit{' '}
          {formatDurationDe(schedule.plannedWorkMinutes)} · Abfahrt{' '}
          {formatDurationDe(schedule.plannedDepartureMinutes)}
        </p>

        {phase === 'ANFAHRT' && onDiscardTravel && (
          <button
            type="button"
            onClick={onDiscardTravel}
            className="mt-1 inline-flex h-11 w-fit items-center justify-center rounded-xl border border-red-300 px-4 text-sm font-medium text-red-600 hover:bg-red-50"
          >
            Anfahrt verwerfen
          </button>
        )}
      </div>
    </div>
  )
}

interface Row {
  label: string
  span: string
  duration: number | null
}

function buildRows(schedule: Schedule): Row[] {
  const rows: Row[] = []
  const { travelStartedAt: t1, actualWorkStartsAt: t2, actualEndsAt: t3, departureEndedAt: t4 } = schedule

  if (t1) {
    rows.push({
      label: 'Anfahrt',
      span: t2 ? `${formatTimeDe(t1)} – ${formatTimeDe(t2)}` : `ab ${formatTimeDe(t1)}`,
      duration: t2 ? minutesBetween(t1, t2) : null,
    })
  }
  if (t2) {
    rows.push({
      label: 'Arbeit',
      span: t3 ? `${formatTimeDe(t2)} – ${formatTimeDe(t3)}` : `ab ${formatTimeDe(t2)}`,
      duration: t3 ? Math.max(1, minutesBetween(t2, t3)) : null,
    })
  }
  if (t3 && schedule.plannedDepartureMinutes > 0) {
    rows.push({
      label: 'Abfahrt',
      span: t4 ? `${formatTimeDe(t3)} – ${formatTimeDe(t4)}` : `ab ${formatTimeDe(t3)}`,
      duration: t4 ? minutesBetween(t3, t4) : null,
    })
  }

  return rows
}

/** ISO anchor of the running phase — same rule `useWorkerAppointment` feeds the primary button. */
function phaseAnchor(schedule: Schedule, phase: WorkPhase | null): string | null {
  if (phase === 'ANFAHRT') return schedule.travelStartedAt
  if (phase === 'ARBEIT') return schedule.actualWorkStartsAt
  if (phase === 'ABFAHRT') return schedule.actualEndsAt
  return null
}

function plannedMinutesForPhase(schedule: Schedule, phase: WorkPhase | null): number {
  if (phase === 'ANFAHRT') return schedule.plannedTravelMinutes
  if (phase === 'ARBEIT') return schedule.plannedWorkMinutes
  if (phase === 'ABFAHRT') return schedule.plannedDepartureMinutes
  return 0
}
